import React, { useEffect, useRef, useState } from "react";
import GoBackButton from "shared/ui/back-button";
import "./style.scss";
import { PATH_LIST } from "shared/lib/react-router";
import { onboardModel } from "entities/onboard";
import { PasswordField } from "widgets/auth/components";
import { Form, Formik } from "formik";
import ValidationCheckbox from "pages/onboarding/components/validation-checkbox";
import Button from "shared/ui/button";
import { useNavigate } from "react-router-dom";
import { ResponseCodes, sircapApi } from "shared/api/sircap";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { sessionModel } from "entities/session";
import { useStore } from "zustand";
import OtpInput from "widgets/otp-input";
import { accountStore } from "entities/account";

export const ForgotPasswordVerifyPage = () => {
  const [timer, setTimer] = useState(59);
  const [resend, setResend] = useState(false);
  const navigate = useNavigate();
  const id = onboardModel.useRegisterId();
  const phoneNumber = onboardModel.useRegisterPhone();
  const registerType = onboardModel.useRegisterType();
  const { i18n } = useTranslation();
  const { t } = useTranslation("onboarding-auth");
  const { t: err } = useTranslation("errors");
  const { t: y } = useTranslation("yup");
  const { syncUser, clear } = useStore(onboardModel.onboardStore);
  const { synckyc, synckyb } = useStore(accountStore);
  const { addUser } = useStore(sessionModel.sessionStore);
  const [isLoading, setLoading] = useState(false);

  const { setLink } = useStore(sessionModel.sessionStore);
  useEffect(() => {
    setLink(PATH_LIST.forgotPassword);

    return () => setLink("");
  }, []);

  useEffect(() => {
    if (!resend && timer > 0) {
      const interval = setInterval(() => {
        setTimer(timer - 1);
      }, 1000);

      return () => {
        clearInterval(interval);
      };
    } else if (!resend && timer === 0) {
      setResend(true);
    }
  }, [timer, resend]);

  const onRegister = async (code: string, pass: string) => {
    setLoading(true);
    try {
      const resp = await sircapApi.auth.codeVerify({
        id: id as number,
        password: pass,
        code: code,
        entity: registerType,
      });
      if (resp.error) {
        switch (resp.error.code) {
          case ResponseCodes.WrongCode:
            setError(true);
            break;

          default:
            toast.error("Server Error");
            setError(true);
            break;
        }
      } else {
        if (resp.data.code === ResponseCodes.SuccessLogin) {
          clear();
          const {
            user,
            accessToken,
            refreshToken,
            entity,
            kyc,
            kyb,
            questionnaireStatus,
            biometrics,
            biometric,
            fcmToken,
            crsData,
            subAccounts,
          } = resp.data.data;
          syncUser(user, entity);
          synckyc(kyc || null);
          synckyb(kyb || null);
          accountStore.setState({ questionnaireStatus });
          addUser(
            user,
            accessToken,
            refreshToken,
            entity,
            biometrics,
            biometric,
            fcmToken,
            crsData,
            subAccounts
          );

          navigate(PATH_LIST.home);
        } else {
          toast.error("Server Error");
        }
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const handleResendClick = async () => {
    const resp = await sircapApi.auth.forgot({
      phone: phoneNumber,
      language: i18n.language,
      entity: registerType,
    });

    if (resp.error) {
      switch (resp.error.code) {
        case ResponseCodes.ServerError:
          toast.error("Server Error");
          break;

        default:
          break;
      }
    } else {
      setResend(false);
      setTimer(59);
    }
  };

  // const AuthInputRef = useRef<AuthCodeRef>(null);
  // const [result, setResult] = useState<string>("");
  const [error, setError] = useState<boolean>(false);

  return (
    <div className="enterTheCodeResetPassword">
      <p className="auth-title">{t("createNewPassword")}</p>

      <Formik
        initialValues={{ code: "", password: "" }}
        onSubmit={(values) => {
          onRegister(values.code, values.password);
        }}
        validate={(values) => {
          const errors: any = {};
          if (values.password.length < 6) {
            errors.password = "Password must be at least 6 characters";
          }
          if (!/[A-Z]/.test(values.password)) {
            errors.password =
              "Password must contain at least 1 upper case letter";
          }
          if (!/[!@#$%^&*()_+{}\[\]:;<>,.?~\-]/.test(values.password)) {
            errors.password = "Password must contain at least 1 symbol";
          }
          if (!/\d/.test(values.password)) {
            errors.password = "Password must contain at least 1 number";
          }
          return errors;
        }}
      >
        {({ values, errors, setFieldValue }) => {
          const isAtLeastSixCharactersValid = values.password.length >= 6;
          const containsUpperCaseValid = /[A-Z]/.test(values.password);
          const containsSymbolValid = /[!@#$%^&*()_+{}\[\]:;<>,.?~\-]/.test(
            values.password
          );
          const containsNumberValid = /\d/.test(values.password);

          return (
            <Form className="formResetPassword">
              <PasswordField
                className="passFieldRePassword"
                label={t("password")}
                id="password"
                isInvalid={!!errors.password}
                placeholder={t("restorePassword")}
              />
              <ValidationCheckbox
                term={t("atLeast6Chars")}
                isChecked={isAtLeastSixCharactersValid}
              />
              <ValidationCheckbox
                term={t("containsUpperCase")}
                isChecked={containsUpperCaseValid}
              />
              <ValidationCheckbox
                term={t("containsSymbol")}
                isChecked={containsSymbolValid}
              />
              <ValidationCheckbox
                term={t("containsNumber")}
                isChecked={containsNumberValid}
              />

              <div className="codePartResatPassword codePartResatPasswordFP">
                <span>
                  {t("smsSentTo")} +{phoneNumber}
                </span>

                {/* <AuthCode
                  allowedCharacters="numeric"
                  length={4}
                  onChange={handleOnChange}
                  ref={AuthInputRef}
                  inputClassName={error ? "red" : isFilled ? "green" : ""}
                  autoFocus={false}
                /> */}

                <OtpInput
                  value={values.code}
                  onChange={(newValue: string) => {
                    setError(false);
                    setFieldValue("code", newValue);
                    // setResult(newValue);
                  }}
                  disabled={isLoading}
                  error={error}
                  isFilled={values.code.length === 4}
                  shouldAutoFocus={true}
                />

                {error && (
                  <span className="error-verify">{y("codeInvalid")}</span>
                )}

                <span className="codeTextRe">
                  {!resend ? (
                    <>
                      {t("sendCodeAgain")}...{timer}{" "}
                    </>
                  ) : (
                    <span onClick={handleResendClick} className="resendCode">
                      {t("resendCode")}
                    </span>
                  )}
                </span>
              </div>

              <Button
                type="submit"
                disabled={
                  isLoading ||
                  !containsNumberValid ||
                  !containsSymbolValid ||
                  !containsUpperCaseValid ||
                  !isAtLeastSixCharactersValid ||
                  values.code.length < 4
                }
                variant="dark"
                className="customBtn"
              >
                {t("restorePassword")}
              </Button>

              <span
                className="editYourNumber"
                onClick={() => navigate(PATH_LIST.forgotPassword)}
              >
                {t("editNumber")}
              </span>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};
